import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { toastify } from '../../../../../../utils/toast';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import AnalogMeterImportModal from '../../AnalogMeterImportModal';

const POWER_METERS_URL = '/api/app/power_meter_management/controllers/power_meters';
const POWER_INVOICING_URL = '/api/app/power_meter_management/controllers/invoicing';

const MONTH_OPTIONS = [
    { label: 'January', value: '01' }, { label: 'February', value: '02' },
    { label: 'March', value: '03' }, { label: 'April', value: '04' },
    { label: 'May', value: '05' }, { label: 'June', value: '06' },
    { label: 'July', value: '07' }, { label: 'August', value: '08' },
    { label: 'September', value: '09' }, { label: 'October', value: '10' },
    { label: 'November', value: '11' }, { label: 'December', value: '12' }
];

const getCurrentYearMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Last day of a YYYY-MM month as YYYY-MM-DD (readings are recorded as at month end).
const getMonthEndDate = (yearMonth) => {
    if (!/^\d{4}-\d{2}$/.test(yearMonth || '')) return '';
    const [year, month] = yearMonth.split('-').map(Number);
    return `${yearMonth}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
};

const formatMonthLabel = (yearMonth) => {
    if (!/^\d{4}-\d{2}$/.test(yearMonth || '')) return '';
    const [year, month] = yearMonth.split('-');
    return `${MONTH_OPTIONS.find((m) => m.value === month)?.label || month} ${year}`;
};

const formatKwh = (value) =>
    value !== null && value !== undefined ? `${Number(value).toFixed(2)} kWh` : '-';

// A simple Bootstrap modal shell shared by the dialogs below.
const ModalShell = ({ title, onClose, busy, children }) => (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">{title}</h5>
                    <button type="button" className="btn-close" onClick={onClose} disabled={busy}></button>
                </div>
                {children}
            </div>
        </div>
    </div>
);

const AnalogPowerMetersTab = () => {
    const navigate = useNavigate();
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);
    const [searchTerm, setSearchTerm] = useState('');
    const [rows, setRows] = useState(10);
    const [powerMeters, setPowerMeters] = useState([]);
    const [loading, setLoading] = useState(false);

    // Import meters
    const [showMeterImport, setShowMeterImport] = useState(false);

    // Import readings
    const [showImport, setShowImport] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importYearMonth, setImportYearMonth] = useState(getCurrentYearMonth());
    const [importing, setImporting] = useState(false);

    // Add a single reading
    const [readingMeter, setReadingMeter] = useState(null);
    const [readingYearMonth, setReadingYearMonth] = useState(getCurrentYearMonth());
    const [readingValue, setReadingValue] = useState('');
    const [savingReading, setSavingReading] = useState(false);

    // Readings history
    const [historyMeter, setHistoryMeter] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Generate invoices
    const [showGenerate, setShowGenerate] = useState(false);
    const [generateYear, setGenerateYear] = useState(String(new Date().getFullYear()));
    const [generateMonth, setGenerateMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
    const [generating, setGenerating] = useState(false);
    const [generateResult, setGenerateResult] = useState(null);

    const rowsPerPageOptions = [10, 20, 50, 100];

    useEffect(() => {
        if (selectedFacilityId) {
            fetchAnalogMeters();
        }
    }, [selectedFacilityId]);

    // Analog POWER meters only (the water analog meters are listed under Water Meters).
    const fetchAnalogMeters = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${POWER_METERS_URL}/get_all/${selectedFacilityId}?meterType=analog`,
                'GET'
            );

            if (response.success) {
                const list = response.data?.data;
                setPowerMeters(Array.isArray(list) ? list : []);
            } else {
                setPowerMeters([]);
                toastify(response.error || 'Failed to fetch analog power meters', 'error');
            }
        } catch (error) {
            setPowerMeters([]);
            toastify('Error loading analog power meters', 'error');
        } finally {
            setLoading(false);
        }
    };

    const unitNameOf = (meter) => meter.unitInfo?.unitName || meter.unitName || meter.description || '-';

    const search = searchTerm.toLowerCase();
    const filteredPowerMeters = powerMeters.filter((powerMeter) =>
        !search ||
        powerMeter.meterSerialNumber?.toLowerCase().includes(search) ||
        unitNameOf(powerMeter).toLowerCase().includes(search) ||
        powerMeter.type?.toLowerCase().includes(search) ||
        String(powerMeter.accountNumber ?? '').includes(search)
    );

    const meterStatusTemplate = (rowData) => {
        const statusColors = { 'installed': 'success', 'not installed': 'secondary' };
        const status = rowData.meterStatus || 'installed';
        return (
            <span className={`badge rounded-pill text-bg-${statusColors[status] || 'secondary'}`}>
                {status}
            </span>
        );
    };

    // ----- IMPORT READINGS -----
    const openImport = () => {
        setImportYearMonth(getCurrentYearMonth());
        setImportFile(null);
        setShowImport(true);
    };

    const downloadTemplate = () => {
        let csv = 'serial_number,unit_name,meter_reading\n';
        powerMeters.forEach((meter) => {
            csv += `${meter.meterSerialNumber},"${unitNameOf(meter)}",\n`;
        });
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'analog_power_meter_readings_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleImportSubmit = async (e) => {
        e.preventDefault();
        if (!importYearMonth) {
            toastify('Please select the year and month you are uploading readings for', 'error');
            return;
        }
        if (!importFile) {
            toastify('Please select a CSV file', 'error');
            return;
        }

        try {
            setImporting(true);
            const formData = new FormData();
            // The month goes before the file so the server has it when the file arrives.
            formData.append('yearMonth', importYearMonth);
            formData.append('file', importFile);

            const response = await makeRequest2(
                `${POWER_METERS_URL}/analog_readings_import/${selectedFacilityId}`,
                'POST',
                formData
            );

            if (response.success) {
                const result = response.data || {};
                toastify(
                    result.message || `Readings for ${formatMonthLabel(importYearMonth)} imported successfully`,
                    result.partial ? 'warning' : 'success'
                );
                setShowImport(false);
                setImportFile(null);
                fetchAnalogMeters();
            } else {
                toastify(response.error || 'Failed to import meter readings', 'error');
            }
        } catch (error) {
            toastify('Error importing CSV', 'error');
        } finally {
            setImporting(false);
        }
    };

    // ----- ADD A SINGLE READING -----
    const openAddReading = (meter) => {
        setReadingMeter(meter);
        setReadingYearMonth(getCurrentYearMonth());
        setReadingValue('');
    };

    const handleReadingSubmit = async (e) => {
        e.preventDefault();
        if (readingValue === '' || Number.isNaN(Number(readingValue))) {
            toastify('Enter the meter reading', 'error');
            return;
        }

        try {
            setSavingReading(true);
            const response = await makeRequest2(
                `${POWER_METERS_URL}/analog_readings/${readingMeter._id}`,
                'POST',
                { currentReading: Number(readingValue), yearMonth: readingYearMonth }
            );

            if (response.success) {
                toastify(response.data?.message || 'Reading recorded', 'success');
                setReadingMeter(null);
                fetchAnalogMeters();
            } else {
                toastify(response.error || 'Failed to record reading', 'error');
            }
        } catch (error) {
            toastify('Error recording reading', 'error');
        } finally {
            setSavingReading(false);
        }
    };

    // ----- READINGS HISTORY -----
    const openHistory = async (meter) => {
        setHistoryMeter(meter);
        setHistory([]);
        try {
            setHistoryLoading(true);
            const response = await makeRequest2(`${POWER_METERS_URL}/analog_readings/${meter._id}`, 'GET');
            if (response.success) {
                setHistory(response.data?.data?.monthlyReadings || []);
            } else {
                toastify(response.error || 'Failed to load readings', 'error');
            }
        } catch (error) {
            toastify('Error loading readings', 'error');
        } finally {
            setHistoryLoading(false);
        }
    };

    // ----- GENERATE INVOICES -----
    const generateYearMonth = `${generateYear}-${generateMonth}`;
    const generateYearOptions = (() => {
        const thisYear = new Date().getFullYear();
        return [thisYear - 2, thisYear - 1, thisYear, thisYear + 1].map(String);
    })();

    const openGenerate = () => {
        const now = new Date();
        setGenerateYear(String(now.getFullYear()));
        setGenerateMonth(String(now.getMonth() + 1).padStart(2, '0'));
        setGenerateResult(null);
        setShowGenerate(true);
    };

    const handleGenerateSubmit = async (e) => {
        e.preventDefault();
        try {
            setGenerating(true);
            setGenerateResult(null);
            const response = await makeRequest2(
                `${POWER_INVOICING_URL}/run_facility_billing/${selectedFacilityId}`,
                'POST',
                { yearMonth: generateYearMonth, meterType: 'analog' },
                { timeout: 5 * 60 * 1000 } // billing a whole facility can take a while
            );

            if (response.success) {
                setGenerateResult(response.data);
                if (response.data?.created > 0) {
                    toastify(`${response.data.created} invoice(s) generated for ${formatMonthLabel(generateYearMonth)}`, 'success');
                }
            } else {
                toastify(response.error || 'Failed to generate invoices', 'error');
            }
        } catch (error) {
            toastify('Error generating invoices', 'error');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="card">
            <div className="card-body">
                {/* Top Right Buttons */}
                <div style={{ float: 'right' }}>
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            navigate('/facility/utility_management/meter_management/power_meters/tabs/addAnalogMeter')
                        }
                    >
                        Add Meter
                    </button>
                    &nbsp;&nbsp;
                    <button className="btn btn-outline-primary" onClick={() => setShowMeterImport(true)}>
                        Import Meters
                    </button>
                    &nbsp;&nbsp;
                    <button className="btn btn-primary" onClick={openImport}>
                        Import Meter Readings
                    </button>
                    &nbsp;&nbsp;
                    <button className="btn btn-outline-primary" onClick={openGenerate}>
                        Generate Invoices
                    </button>
                </div>
                <div className="col-md-3 col-xs-12 mb-3">
                    <input
                        className="form-control"
                        placeholder="Search here"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="dt-responsive table-responsive">
                    <DataTable
                        value={filteredPowerMeters}
                        emptyMessage="No analog power meters found."
                        sortMode="multiple"
                        paginator
                        rows={rows}
                        rowsPerPageOptions={rowsPerPageOptions}
                        stripedRows
                        loading={loading}
                        tableStyle={{ minWidth: '50rem' }}
                    >
                        <Column field="meterSerialNumber" header="Meter S/N" sortable></Column>
                        <Column header="Unit" body={unitNameOf}></Column>
                        <Column field="type" header="Meter Type" sortable></Column>
                        <Column header="Account No." body={(rowData) => rowData.accountNumber ?? '-'}></Column>
                        <Column header="Previous (kWh)" body={(rowData) => formatKwh(rowData.previousMeterReading)}></Column>
                        <Column header="Current (kWh)" body={(rowData) => formatKwh(rowData.currentMeterReading)}></Column>
                        <Column field="meterStatus" header="Meter Status" body={meterStatusTemplate}></Column>
                        <Column header="Last Updated" body={(rowData) => rowData.lastUpdated ? new Date(rowData.lastUpdated).toLocaleDateString() : '-'}></Column>
                        <Column
                            header="Actions"
                            body={(rowData) => (
                                <div className="d-flex gap-1">
                                    <Button
                                        icon="ti ti-plus"
                                        className="p-button-rounded p-button-text"
                                        onClick={() => openAddReading(rowData)}
                                        tooltip="Add Reading"
                                    />
                                    <Button
                                        icon="ti ti-list"
                                        className="p-button-rounded p-button-text"
                                        onClick={() => openHistory(rowData)}
                                        tooltip="Readings History"
                                    />
                                    <Button
                                        icon="ti ti-eye"
                                        className="p-button-rounded p-button-text"
                                        onClick={() => navigate(`/facility/utility_management/meter_management/view_power_meter/${rowData._id}`)}
                                        tooltip="View Meter"
                                    />
                                </div>
                            )}
                        />
                    </DataTable>
                </div>
            </div>

            {/* Import (add) analog power meters */}
            {showMeterImport && (
                <AnalogMeterImportModal
                    facilityId={selectedFacilityId}
                    importUrl={`${POWER_METERS_URL}/import_analog_meters`}
                    power
                    onHide={() => setShowMeterImport(false)}
                    onImported={fetchAnalogMeters}
                />
            )}

            {/* Import Meter Readings */}
            {showImport && (
                <ModalShell title="Import Analog Power Readings" onClose={() => setShowImport(false)} busy={importing}>
                    <form onSubmit={handleImportSubmit}>
                        <div className="modal-body">
                            <p>
                                <button type="button" className="btn btn-link p-0" onClick={downloadTemplate}>
                                    Download analog power meters template (.csv)
                                </button>
                            </p>
                            <div className="alert alert-info">
                                <ul className="mb-0">
                                    <li>The CSV lists the facility's analog power meters by <strong>serial_number</strong></li>
                                    <li>Only fill in <strong>meter_reading</strong> (kWh); meters left blank are skipped</li>
                                    <li>Choose the month below: readings are recorded as at the end of that month</li>
                                </ul>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">
                                    Year and month you are uploading for<span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="month"
                                    className="form-control"
                                    value={importYearMonth}
                                    onChange={(e) => setImportYearMonth(e.target.value)}
                                    required
                                />
                                {importYearMonth && (
                                    <small className="text-muted">
                                        Readings will be dated {getMonthEndDate(importYearMonth)} (end of {formatMonthLabel(importYearMonth)})
                                    </small>
                                )}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Upload Completed CSV File</label>
                                <input
                                    type="file"
                                    accept=".csv"
                                    className="form-control"
                                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowImport(false)} disabled={importing}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={importing || !importFile || !importYearMonth}>
                                {importing ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                                Submit
                            </button>
                        </div>
                    </form>
                </ModalShell>
            )}

            {/* Add a single reading */}
            {readingMeter && (
                <ModalShell title={`Add Reading - ${readingMeter.meterSerialNumber}`} onClose={() => setReadingMeter(null)} busy={savingReading}>
                    <form onSubmit={handleReadingSubmit}>
                        <div className="modal-body">
                            <p className="text-muted mb-3">
                                Unit: <strong>{unitNameOf(readingMeter)}</strong> &nbsp;|&nbsp; Last reading:{' '}
                                <strong>{formatKwh(readingMeter.currentMeterReading)}</strong>
                            </p>
                            <div className="mb-3">
                                <label className="form-label">
                                    Year and month<span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="month"
                                    className="form-control"
                                    value={readingYearMonth}
                                    onChange={(e) => setReadingYearMonth(e.target.value)}
                                    required
                                />
                                {readingYearMonth && (
                                    <small className="text-muted">
                                        Recorded as at {getMonthEndDate(readingYearMonth)} (end of {formatMonthLabel(readingYearMonth)})
                                    </small>
                                )}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">
                                    Meter reading (kWh)<span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="number"
                                    className="form-control"
                                    step="0.01"
                                    min="0"
                                    value={readingValue}
                                    onChange={(e) => setReadingValue(e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={() => setReadingMeter(null)} disabled={savingReading}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={savingReading || !readingYearMonth || readingValue === ''}>
                                {savingReading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                                Save Reading
                            </button>
                        </div>
                    </form>
                </ModalShell>
            )}

            {/* Readings history */}
            {historyMeter && (
                <ModalShell title={`Readings - ${historyMeter.meterSerialNumber}`} onClose={() => setHistoryMeter(null)}>
                    <div className="modal-body">
                        <div className="dt-responsive table-responsive">
                            <DataTable
                                value={history}
                                loading={historyLoading}
                                emptyMessage="No readings recorded yet."
                                stripedRows
                                paginator
                                rows={6}
                            >
                                <Column field="yearMonth" header="Month" sortable></Column>
                                <Column header="Opening" body={(r) => formatKwh(r.initialReading)}></Column>
                                <Column header="Closing" body={(r) => formatKwh(r.finalReading)}></Column>
                                <Column header="Usage" body={(r) => formatKwh(r.consumption)}></Column>
                            </DataTable>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" onClick={() => setHistoryMeter(null)}>
                            Close
                        </button>
                    </div>
                </ModalShell>
            )}

            {/* Generate invoices */}
            {showGenerate && (
                <ModalShell title="Generate Invoices" onClose={() => setShowGenerate(false)} busy={generating}>
                    <form onSubmit={handleGenerateSubmit}>
                        <div className="modal-body">
                            <p className="text-muted">
                                Choose the year and month to bill. An invoice is created for every analog power meter
                                with a customer account that has a reading for that month.
                            </p>
                            <div className="row mb-3">
                                <div className="col-6">
                                    <label className="form-label">Year</label>
                                    <select
                                        className="form-select"
                                        value={generateYear}
                                        onChange={(e) => setGenerateYear(e.target.value)}
                                        disabled={generating}
                                    >
                                        {generateYearOptions.map((year) => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-6">
                                    <label className="form-label">Month</label>
                                    <select
                                        className="form-select"
                                        value={generateMonth}
                                        onChange={(e) => setGenerateMonth(e.target.value)}
                                        disabled={generating}
                                    >
                                        {MONTH_OPTIONS.map((month) => (
                                            <option key={month.value} value={month.value}>{month.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <small className="text-muted d-block">
                                The power charges (tariff) for the month must be set up, or no invoice can be created.
                            </small>

                            {generateResult && (
                                <div className={`alert mt-3 mb-0 ${generateResult.created > 0 ? 'alert-success' : 'alert-warning'}`}>
                                    <strong>{formatMonthLabel(generateYearMonth)}</strong>
                                    <div>Invoices created: <strong>{generateResult.created}</strong></div>
                                    <div>Skipped (already invoiced / not postpaid): <strong>{generateResult.skipped}</strong></div>
                                    <div>Errors: <strong>{generateResult.errors}</strong></div>
                                    {generateResult.reasons && <div className="mt-1">Why: {generateResult.reasons}</div>}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowGenerate(false)} disabled={generating}>
                                {generateResult ? 'Close' : 'Cancel'}
                            </button>
                            <button type="submit" className="btn btn-success" disabled={generating}>
                                {generating ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                                Generate Invoices
                            </button>
                        </div>
                    </form>
                </ModalShell>
            )}
        </div>
    );
};

export default AnalogPowerMetersTab;
